import { QuestionCircleOutlined } from '@ant-design/icons'
import { Flex, Form, InputNumber, Select, Switch, Tooltip } from 'antd'
import { BoosterRewardType } from 'Modules'

import {
  BOOSTER_PROGRAM_FIELD_MAX_LENGTH,
  BOOSTER_PROGRAM_MESSAGES,
  BOOSTER_TOKEN_TYPE_OPTIONS,
} from 'constants/booster-program'

import { HorizontalRow } from './horizontal-row'
import { buildName } from './utils'

const MAX = BOOSTER_PROGRAM_FIELD_MAX_LENGTH

const SOLD_OUT_TOOLTIP =
  'When the switch is OFF, users who buy PTO and apply promo code can claim cashback/bonus immediately. When the switch is ON, users who buy PTO and apply promo code can claim cashback/bonus once PTO is sold out.'

interface Props {
  /** When provided, names are nested under this Form.List index. When omitted, names are top-level. */
  parentName?: number
  rewardType?: BoosterRewardType
}

const minRule = (label: string) => ({
  type: 'number' as const,
  min: 1,
  message: BOOSTER_PROGRAM_MESSAGES.E01(label),
})

const SoldOutRow = ({ parentName }: { parentName: number | undefined }) => (
  <HorizontalRow
    label={
      <>
        PTO Sold Out{' '}
        <Tooltip title={SOLD_OUT_TOOLTIP} placement="right">
          <QuestionCircleOutlined style={{ color: '#9ca3af' }} />
        </Tooltip>
      </>
    }
  >
    <Form.Item
      name={buildName(parentName, 'ptoSoldOut')}
      valuePropName="checked"
      style={{ marginBottom: 0 }}
    >
      <Switch />
    </Form.Item>
  </HorizontalRow>
)

export const MilestoneRewardFields = ({ parentName, rewardType }: Props) => {
  if (!rewardType) return null

  if (rewardType === 'DISCOUNT_CODE') {
    return (
      <HorizontalRow label="Discount Value" required>
        <Flex gap={8} align="center">
          <Form.Item
            name={buildName(parentName, 'discountValue')}
            rules={[
              { required: true, message: 'Discount Value is required' },
              minRule('Discount value'),
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              maxLength={MAX.discountValue}
              placeholder="10"
              style={{ width: 100 }}
            />
          </Form.Item>
          <span className="booster-program-create__inline-label">%</span>
        </Flex>
      </HorizontalRow>
    )
  }

  if (rewardType === 'APR_BOOSTER_CODE') {
    return (
      <HorizontalRow label="Boost APR Value" required>
        <Flex gap={8} align="center">
          <Form.Item
            name={buildName(parentName, 'boostAprValue')}
            rules={[
              { required: true, message: 'Boost APR Value is required' },
              minRule('Boost APR value'),
            ]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              maxLength={MAX.boostAprValue}
              placeholder="5"
              prefix="+"
              style={{ width: 100 }}
            />
          </Form.Item>
          <span className="booster-program-create__inline-label">%</span>
        </Flex>
      </HorizontalRow>
    )
  }

  if (rewardType === 'CASHBACK_TOKEN_CODE') {
    return (
      <>
        <HorizontalRow label="Cashback Rate" required>
          <Flex gap={8} align="center">
            <Form.Item
              name={buildName(parentName, 'cashbackRate')}
              rules={[
                { required: true, message: 'Cashback Rate is required' },
                minRule('Cashback rate'),
              ]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                maxLength={MAX.cashbackRate}
                placeholder="5"
                style={{ width: 100 }}
              />
            </Form.Item>
            <span className="booster-program-create__inline-label">%</span>
          </Flex>
        </HorizontalRow>
        <HorizontalRow label="Token Type" required>
          <Form.Item
            name={buildName(parentName, 'tokenType')}
            rules={[{ required: true, message: 'Token Type is required' }]}
            style={{ marginBottom: 0, maxWidth: 280 }}
          >
            <Select options={BOOSTER_TOKEN_TYPE_OPTIONS} />
          </Form.Item>
        </HorizontalRow>
        <SoldOutRow parentName={parentName} />
      </>
    )
  }

  // PTO_BONUS_CODE
  return (
    <>
      <HorizontalRow label="Bonus Value" required>
        <Flex gap={8} align="center" wrap="wrap">
          <span className="booster-program-create__inline-label">Buy</span>
          <Form.Item
            name={buildName(parentName, 'bonusBuy')}
            rules={[{ required: true, message: 'Bonus Value is required' }, minRule('Bonus value')]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              maxLength={MAX.bonusValue}
              placeholder="150"
              style={{ width: 100 }}
            />
          </Form.Item>
          <span className="booster-program-create__inline-label">get</span>
          <Form.Item
            name={buildName(parentName, 'bonusGet')}
            rules={[{ required: true, message: 'Bonus Value is required' }, minRule('Bonus value')]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              maxLength={MAX.bonusValue}
              placeholder="15"
              style={{ width: 100 }}
            />
          </Form.Item>
          <span className="booster-program-create__inline-label">PTO</span>
        </Flex>
      </HorizontalRow>
      <SoldOutRow parentName={parentName} />
    </>
  )
}
